<template>
    <div class="pb-8">
        <div class="mb-6">
            <PageTitle title="Gestion des catégories" />
        </div>

        <!-- Liste des catégories -->
        <CategoriesList
            :categories="categories"
            :loading="loading"
            :pagination="pagination"
            @search="handleSearch"
            @add="showAddModal = true"
            @edit="editCategory"
            @delete="confirmDelete"
            @page-change="goToPage"
        />

        <!-- Modal d'ajout/modification de catégorie -->
        <BaseModal
            :model-value="showAddModal || showEditModal"
            :title="showEditModal ? 'Modifier la catégorie' : 'Ajouter une catégorie'"
            size="md"
            show-cancel-button
            show-confirm-button
            :cancel-text="'Annuler'"
            :confirm-text="showEditModal ? 'Mettre à jour' : 'Ajouter'"
            :confirm-loading="formSubmitting"
            @confirm="handleSubmit"
            @cancel="closeModals"
            @update:model-value="closeModals"
        >
            <form @submit.prevent="handleSubmit" class="space-y-5">
                <!-- Nom de la catégorie -->
                <BaseInput
                    id="categoryName"
                    v-model="categoryForm.name"
                    type="text"
                    label="Nom de la catégorie*"
                    placeholder="Entrez le nom de la catégorie"
                    :error="validationErrors.name ? validationErrors.name[0] : null"
                    required
                />

                <!-- Catégorie parente -->
                <BaseSelect
                    id="parentCategory"
                    v-model="categoryForm.parent_category_id"
                    label="Catégorie parente"
                    :options="parentCategoryOptions"
                />

                <!-- Description -->
                <div>
                    <label for="categoryDescription" class="block mb-2 font-medium text-gray-700 text-sm">
                        Description
                    </label>
                    <textarea
                        id="categoryDescription"
                        v-model="categoryForm.description"
                        placeholder="Décrivez votre catégorie ici..."
                        class="w-full px-3.5 py-3 text-sm text-gray-800 bg-gray-50 border border-gray-300 rounded-lg transition-all focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none resize-none"
                        :class="{ 'border-danger': validationErrors.description }"
                        rows="3"
                    ></textarea>
                    <span v-if="validationErrors.description" class="block text-danger text-xs mt-1.5">
                        {{ validationErrors.description[0] }}
                    </span>
                </div>
            </form>
        </BaseModal>

        <!-- Modal de confirmation de suppression -->
        <BaseModal
            v-model="showDeleteModal"
            title="Confirmer la suppression"
            size="sm"
            show-cancel-button
            show-confirm-button
            cancel-text="Annuler"
            confirm-text="Supprimer"
            confirm-variant="danger"
            :confirm-loading="deleteLoading"
            @confirm="deleteCategory"
            @cancel="showDeleteModal = false"
        >
            <div class="text-center py-2">
                <div class="w-16 h-16 mx-auto mb-4 text-danger">
                    <BaseIcon name="delete" size="64" color="#dc2626" />
                </div>
                <p class="text-gray-600 mb-2">
                    Êtes-vous sûr de vouloir supprimer la catégorie <strong>{{ categoryToDelete?.name }}</strong> ?
                </p>
                <p v-if="hasCategoryProducts" class="text-danger text-sm mt-3">
                    Cette catégorie contient des produits. Sa suppression affectera ces produits.
                </p>
                <p v-if="hasCategoryChildren" class="text-danger text-sm mt-2">
                    Cette catégorie contient des sous-catégories. Sa suppression affectera ces sous-catégories.
                </p>
            </div>
        </BaseModal>
    </div>
</template>

<script>
import { categoriesService } from '@/services';
import CategoriesList from '@/views/categories/CategoriesList.vue';
import { useToastStore } from '@/store/modules';

export default {
    name: 'CategoriesView',
    components: {
        CategoriesList
    },
    data() {
        return {
            // Données des catégories
            categories: [],

            // UI states
            loading: false,
            error: null,
            showAddModal: false,
            showEditModal: false,
            showDeleteModal: false,
            formSubmitting: false,
            deleteLoading: false,

            // Formulaire et sélection
            categoryForm: {
                name: '',
                description: '',
                parent_category_id: ''
            },
            categoryToDelete: null,
            validationErrors: {},

            // Pagination
            pagination: {
                currentPage: 1,
                perPage: 10,
                total: 0,
                lastPage: 1
            },

            // Filtres
            filters: {
                search: '',
                parent_category_id: null,
                only_root: 0,
                sort_by: 'name',
                sort_direction: 'asc'
            }
        }
    },
    computed: {
        parentCategories() {
            // Filtrer les catégories pour ne pas inclure la catégorie en cours d'édition ni ses enfants
            if (!this.showEditModal) return this.categories;

            const editingId = this.categoryForm.id;
            const excludeIds = [editingId];

            // Fonction récursive pour trouver tous les enfants d'une catégorie
            const findChildrenIds = (parentId) => {
                const children = this.categories.filter(cat => cat.parent_category_id === parentId);
                children.forEach(child => {
                    excludeIds.push(child.id);
                    findChildrenIds(child.id);
                });
            };

            findChildrenIds(editingId);

            return this.categories.filter(cat => !excludeIds.includes(cat.id));
        },
        parentCategoryOptions() {
            const options = [{ value: '', label: 'Aucune (catégorie racine)' }];
            this.parentCategories.forEach(category => {
                options.push({ value: category.id, label: category.name });
            });
            return options;
        },
        hasCategoryProducts() {
            return this.categoryToDelete && this.categoryToDelete.products_count > 0;
        },
        hasCategoryChildren() {
            return this.categoryToDelete &&
                this.categories.some(cat => cat.parent_category_id === this.categoryToDelete.id);
        }
    },
    created() {
        this.loadCategories();
    },
    methods: {
        async loadCategories() {
            this.loading = true;
            this.error = null;

            try {
                // Préparer les paramètres pour l'API
                const params = {
                    page: this.pagination.currentPage,
                    per_page: this.pagination.perPage,
                    search: this.filters.search,
                    parent_category_id: this.filters.parent_category_id,
                    only_root: this.filters.only_root,
                    sort_by: this.filters.sort_by,
                    sort_direction: this.filters.sort_direction,
                    with: ['products'] // Ajouter les produits pour le comptage
                };

                // Appel au service
                const response = await categoriesService.getCategories(params);

                // Extraire les données
                if (response.data.current_page) {
                    // Format de pagination Laravel
                    this.categories = response.data.data;
                    this.pagination.currentPage = response.data.current_page;
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.lastPage = response.data.last_page;
                } else {
                    // Format simple
                    this.categories = response.data;
                }
            } catch (err) {
                this.error = "Erreur lors du chargement des catégories";
                console.error(err);
            } finally {
                this.loading = false;
            }
        },

        // Gestion du filtre de recherche
        handleSearch(search) {
            this.filters.search = search;
            this.pagination.currentPage = 1;
            this.loadCategories();
        },

        // Pagination
        goToPage(page) {
            if (page < 1 || page > this.pagination.lastPage) return;
            this.pagination.currentPage = page;
            this.loadCategories();
        },

        // Réinitialisation du formulaire
        resetForm() {
            this.categoryForm = {
                name: '',
                description: '',
                parent_category_id: ''
            };
            this.validationErrors = {};
        },

        // Fermeture des modaux
        closeModals() {
            this.showAddModal = false;
            this.showEditModal = false;
            this.showDeleteModal = false;
            this.resetForm();
        },

        // Édition d'une catégorie
        editCategory(category) {
            this.categoryForm = { ...category };
            this.showEditModal = true;
        },

        // Validation du formulaire
        validateForm() {
            let isValid = true;
            this.validationErrors = {};

            // Validation du nom
            if (!this.categoryForm.name) {
                this.validationErrors.name = ['Le nom de la catégorie est requis'];
                isValid = false;
            } else if (this.categoryForm.name.length > 100) {
                this.validationErrors.name = ['Le nom ne doit pas dépasser 100 caractères'];
                isValid = false;
            }

            return isValid;
        },

        // Soumission du formulaire
        async handleSubmit() {
            if (!this.validateForm()) return;

            this.formSubmitting = true;

            try {
                if (this.showEditModal) {
                    // Modification d'une catégorie existante
                    const categoryId = this.categoryForm.id;
                    await categoriesService.updateCategory(categoryId, {
                        name: this.categoryForm.name,
                        description: this.categoryForm.description,
                        parent_category_id: this.categoryForm.parent_category_id || null
                    });

                    useToastStore().addToast({
                        type: 'success',
                        message: 'Catégorie mise à jour avec succès!',
                        duration: 6000
                    })
                } else {
                    // Création d'une nouvelle catégorie
                    await categoriesService.createCategory({
                        name: this.categoryForm.name,
                        description: this.categoryForm.description,
                        parent_category_id: this.categoryForm.parent_category_id || null
                    });

                    useToastStore().addToast({
                        type: 'success',
                        message: 'Catégorie créée avec succès!',
                        duration: 6000
                    })
                }

                // Fermer le modal et recharger les données
                this.closeModals();
                await this.loadCategories();
            } catch (err) {
                console.error('Erreur lors de la création/modification de la catégorie:', err);

                if (err.response && err.response.status === 422) {
                    this.validationErrors = err.response.data.errors || {};
                } else {
                    useToastStore().addToast({
                        type: 'error',
                        message: 'Une erreur est survenue, veuillez réessayer.',
                        duration: 6000
                    })
                }
            } finally {
                this.formSubmitting = false;
            }
        },

        // Confirmation de suppression
        confirmDelete(category) {
            this.categoryToDelete = category;
            this.showDeleteModal = true;
        },

        // Suppression d'une catégorie
        async deleteCategory() {
            if (!this.categoryToDelete) return;

            this.deleteLoading = true;

            try {
                await categoriesService.deleteCategory(this.categoryToDelete.id);

                useToastStore().addToast({
                    type: 'success',
                    message: 'Catégorie supprimée avec succès!',
                    duration: 6000
                })

                this.showDeleteModal = false;
                this.categoryToDelete = null;
                await this.loadCategories();
            } catch (err) {
                console.error('Erreur lors de la suppression de la catégorie:', err);

                useToastStore().addToast({
                    type: 'error',
                    message: 'Une erreur est survenue lors de la suppression.',
                    duration: 6000
                })
            } finally {
                this.deleteLoading = false;
            }
        }
    }
}
</script>

<style scoped>
/* Minimal scoped styles - most styling is done with Tailwind */
</style>
